import { useContext, useState } from 'react';

import { resetPassword } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import CustomButton from '../../../../../shared/components/Buttons/CustomButton/CustomButton';
import IconButton from '../../../../../shared/components/Buttons/IconButton/IconButton';
import TextButton from '../../../../../shared/components/Buttons/TextButton/TextButton';
import CustomInput from '../../../../../shared/components/Inputs/CustomInput/CustomInput';
import { RECAPTCHA_SITE_KEY, RESET_PASSWORD_ROUTE } from '../../../../../shared/constants/auth';
import { ARROW_LEFT_ICON, PASSWORD_HIDE_ICON, PASSWORD_SHOW_ICON } from '../../../../../shared/constants/images';
import { CLICK_ACTION, FORGOT_PASSWORD_PAGE, PASSWORD_CONTINUE } from '../../../../../shared/constants/tagManager';
import { AuthContext } from '../../../../../shared/hooks/useAuth/useAuth';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_AUTH } from '../../../../../shared/translations/auth';
import type { GenericVoidFunction } from '../../../../../shared/types/genericTypes';
import './EnterPassword.scss';

interface EnterPasswordProps {
  email: string;
  changeStep: GenericVoidFunction;
}

const EnterPassword = ({ email, changeStep }: EnterPasswordProps) => {
  const [userPassword, setUserPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loadingForgot, setLoadingForgot] = useState(false);
  const [loading, setLoading] = useState(false);

  const { sessionLogin, errorCode, resetError } = useContext(AuthContext);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const error =
    errorCode.toString().includes('NotAuthorizedException') ||
    (errorCode.toString() !== '' && errorCode.toString() !== '0');

  const errorMessage = error ? t(TRANSLATIONS_AUTH.enterPassword.invalidCredentials) : '';

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserPassword(e.target.value);
    resetError('');
  };

  const handleSubmitSignIn = async () => {
    TagManagerService.pushEvent(CLICK_ACTION, PASSWORD_CONTINUE);
    globalThis.grecaptcha.ready(async () => {
      setLoading(true);
      try {
        const recaptchaToken = await globalThis.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' });
        await sessionLogin(email, userPassword, recaptchaToken);
      } finally {
        setLoading(false);
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmitSignIn();
    }
  };

  const handleEyeClick = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPassword = async () => {
    resetError('');
    setLoadingForgot(true);
    TagManagerService.pushEvent(CLICK_ACTION, FORGOT_PASSWORD_PAGE);
    await resetPassword({ username: email });
    navigate(RESET_PASSWORD_ROUTE, { state: { email } });
    setLoadingForgot(false);
  };

  const handleGoBack = () => {
    resetError('');
    changeStep(0);
  };

  return (
    <div className="enter-password flex flex-col items-center">
      <div className="auth__card enter-password__card">
        <div className="auth__go-back">
          <TextButton variant="primary" action={handleGoBack} iconLeft={ARROW_LEFT_ICON}>
            {t(TRANSLATIONS_AUTH.goBack)}
          </TextButton>
        </div>

        <div className="enter-password__text-container flex flex-col">
          <div className="enter-password__title">{t(TRANSLATIONS_AUTH.enterPassword.title)}</div>
          <div className="enter-password__description">{t(TRANSLATIONS_AUTH.enterPassword.description)}</div>
        </div>
        <CustomInput
          id="login-password"
          name="login-password"
          placeholder={t(TRANSLATIONS_AUTH.enterPassword.placeholder)}
          type={showPassword ? 'text' : 'password'}
          value={userPassword}
          onChange={handleChangePassword}
          onKeyDown={handleKeyDown}
          error={errorMessage}
          suffix={
            <IconButton
              icon={showPassword ? PASSWORD_HIDE_ICON : PASSWORD_SHOW_ICON}
              action={handleEyeClick}
              variant="transparent"
              size="s"
              label="Toggle password visibility"
            />
          }
          containerClassName="auth__input"
        />

        <div className="enter-password__text-button">
          <TextButton variant="accent" size="xs" action={handleForgotPassword} loading={loadingForgot}>
            {t(TRANSLATIONS_AUTH.enterPassword.forgotPassword)}
          </TextButton>
        </div>
        <CustomButton
          size="l"
          variant="primary"
          onClick={handleSubmitSignIn}
          disabled={!userPassword}
          loading={loading}
        >
          {t(TRANSLATIONS_AUTH.enterPassword.button)}
        </CustomButton>
      </div>
    </div>
  );
};

export default EnterPassword;
